import { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  FlagIcon,
  TableIconsCell,
  TableWrapperFooter,
  Pagination,
  Chip,
  SimpleTable,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { usePaginationState, useStatesWithFilterCounter, useUserVisitsQuery } from '../../hooks';
import { momentTextFormat } from '../../utils';
import { useStore } from '../../context';
import { TABLE } from '../../const';
import { useNavigate } from 'react-router-dom';
import { i18n } from '../../i18n';

interface UserVisitsPageProps {
  innerScroll?: boolean;
}

export const UserVisitsPage = styled.div<UserVisitsPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const UserVisitsHeader = ({ totalCount }: any) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.userVisits.title', { totalCount })}
      </Typography>
    </DashboardHeader>
  );
};

export const UserVisitsBody = ({ setTotalCount }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    username: '',
    belongsTo: 'All',
    country: '',
    lastVisitedAt: 'All',
  });

  const { data = { data: [], totalCount: 0 }, isFetching } = useUserVisitsQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleOnPageChange = (e: any, page: number) => {
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleRowClick = (to: string) => {
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      tableRowProps={(visit, i) => ({
        key: i,
        onClick: () => handleRowClick(`/dashboard/user-visits/${visit.userId}`),
      })}
      columns={[
        {
          headText: () => i18n.t('label.userName'),
          body: (visit) => (
            <TableCell component={'th'} align={'left'}>
              {visit.user.firstName} {visit.user.lastName}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.role'),
          bodyText: (visit) => visit.role,
        },
        {
          headText: () => i18n.t('common.client'),
          bodyText: (visit) => visit.clientCode,
        },
        {
          headText: () => i18n.t('label.email'),
          bodyText: (visit) => visit.user.email,
        },
        {
          headText: () => i18n.t('label.lastVisitedAt'),
          body: (visit) => (
            <TableCell align={'left'}>
              {visit.status === 'ONLINE' ? (
                <Chip size={'small'} label={i18n.t('label.chip.online')} color={'green'} />
              ) : (
                momentTextFormat(visit.lastVisitedAt, user)
              )}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.country'),
          body: (visit) => (
            <TableIconsCell align={'left'} withGap>
              <FlagIcon countryCode={visit.countryCode} />
              {visit.country}
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('label.ip'),
          bodyText: (visit) => visit.ip,
        },
        {
          headText: () => i18n.t('label.device'),
          bodyText: (visit) => visit.deviceName,
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handleOnPageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
